export default class TemplateService {
  _templateData = {template: {}};
  _dummyControl = {
    'control': {
      'type': "not-found",
      'getter': "noOp",
      'setter': "noOp"
    }
  }
  
  constructor(RouteResolverService, FlashService, AuthService, EventService, FEService, uuid, $state) {
    'ngInject';
    this.rrs = RouteResolverService;
    this.fl = FlashService;
    this.es = EventService;
    this.as = AuthService;
    this.fe = FEService;
    this.uuid = uuid;
    this.$state = $state;
    this.activeFolderId = '';
    this.subscribeEvents();
  }
  
  initialize() {
    this.clipBoard = {};
    this.clearTemplateObjects(this._templateData);
    this._originalTemplateData = null;
    this._templateId = null;
    this._prevState = null;
    this._templateDataPromise = null;
    this._templateHasChanges = false;
    this._newConnection = null;
    this._objectStatuses = { updated: "updated", removed: "removed", new: "new", current: "current" };
  }

  // not a fan of the getters,`template = await this.getTemplate()` is safer - J
  get templateId() { return this._templateId } 
  get template() { return this._templateData?.template || {} }
  get pages() { return this.template.pages || [] } 
  get references() { return this.template.references || [] } 
  get tokens() { return this.template.tokens || [] } 
  get relations() { return this.template.relations || [] } 
  get partialPages() { return this.template.partialPages || [] } 
  get behaviors() { return this.template.behaviors || [] } 
  get conditions() { return this.template.conditions || [] } 
  get originalTemplateData() { return this._originalTemplateData || {} } 
  get objectStatuses() { return this._objectStatuses }
  get PROVIDER() {
    return (async () => {
      this._PROVIDER = this._PROVIDER || await this.as.getProviderSettings(this.template);
      return this._PROVIDER;
    })();
  }

  tokenIsMultiCheckboxOption(token) {
    return token?.control?.type === 'multicheckbox-control' && !!token?.options?.choice;
  }

  tokenIsBoolean(provider, token) {
    const fieldType = token?.fieldSchema?.fieldType ?? token?.tokenType;
    return !!provider?.booleanFields?.includes(fieldType) || this.tokenIsMultiCheckboxOption(token);
  }

  toRaw(options = {}) {
    const template = options.template || this.template;
    const rawTemplate = Object.keys(template).reduce((acc, prop) => {
      if (!(template[prop] instanceof Array)) acc[prop] = template[prop];
      return acc;
    }, {});
    if (options.includeRefs) rawTemplate.references = (template.references || []).map(ref => { 
      return { id: ref.id, name: ref.name, tableId: ref.tableId, tableSchema: ref.tableSchema, _temp: ref._temp, _new: ref._new };
    });
    if (options.includeRels) rawTemplate.relations = (template.relations || []).map(rel => { 
      return { id: rel.id, childId: rel.childId, fieldId: rel.fieldId, parentId: rel.parentId, _temp: rel._temp };
    });
    return Object.keys(rawTemplate).length ? rawTemplate : null;
  }
  
  getTemplateData(options) { //: Promise<TemplateData>
    try {
      const templateId = options?.templateId || this.rrs.templateId;
      if (!templateId) return null;

      if (
        templateId !== this._templateId
        || this.$state.current.name !== this._prevState
      ) {
        this.initialize();
      }
      this._prevState = this.$state.current.name;
      
      if (!this._templateDataPromise) {
        this._templateId = templateId;
        this._templateDataPromise = this.loadTemplateData(templateId, {
          includeInactive: options?.includeInactive,
        });
      }
      return this._templateDataPromise;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Convert a full templateDTO to a slimmed-down structure.
   * Keeps only:
   * - Template: id, title, description, content, layout, references, tokens
   * - Reference: id, name, tableId, tableSchema.fields
   * - Field: id, label, fieldType
   * - Token: id, layout, referenceId, fieldId, options (mapped from token.control)
   */
  toLLMDTO() {
    
    const dto = this.template;

    const toStr = (v) => (v == null ? '' : String(v));

    const references = Array.isArray(dto && dto.references)
      ? dto.references.map((r) => ({
        id: toStr(r && r.id),
        name: toStr(r && r.name),
        tableId: toStr(r && r.tableId),
        tableSchema: {
          fields: Array.isArray(r && r.tableSchema && r.tableSchema.fields)
            ? r.tableSchema.fields.map((f) => ({
              id: toStr(f && f.id),
              label: toStr(f && f.label),
              fieldType: toStr(f && f.fieldType),
            }))
            : [],
        },
      }))
      : [];

    const tokens = Array.isArray(dto && dto.tokens)
      ? dto.tokens.map((t) => ({
        id: toStr(t && t.id),
        layout: toStr(t && t.layout),
        referenceId: toStr(t && t.referenceId),
        fieldId: toStr(t && t.fieldId),
      }))
      : [];

    return {
      id: toStr(dto && dto.id),
      title: toStr(dto && dto.title),
      description: toStr(dto && dto.description),
      content: toStr(dto && dto.content),
      layout: toStr(dto && dto.layout),
      userEmail: this.as.getProfile()?.email,
      realm: toStr(this.template.realm),
      provider: toStr(this.template.provider),
      references,
      tokens,
    };
  }


  async loadTemplateData(templateId, options = {}) {
    const response = await this.getTemplateById(templateId, options);
    // Can happen when stored conn is not valid, for example
    if (!response) return null;
    this.setTemplateData(response.data || {});
    return this._templateData;
  }
  
  async getTemplate() {
    const {template} = await this.getTemplateData();
    return template;
  }
  
  getTemplateById(templateId, options = {}) {
    const includeInactive = options.includeInactive === true;
    const request = this.as.includeConnectionInRequest({
      templateId,
      includeInactive,
    });
    return this.fe.httpsCallable('getTemplateById', request);
  }

  async getTemplateConnection() {
    // null when not logged in
    const idToken = await this.as.idToken();
    const contextConn = idToken?.claims?.connectionString;
    const supplied = this.as.suppliedConnection();

    const connIdParam = this.template.connectionId ? `connectionId=${this.template.connectionId}` : "";
    const contextParam = contextConn ? `contextConnection=${encodeURIComponent(contextConn)}` : "";
    const suppliedParam = supplied ? `suppliedConnection=${encodeURIComponent(JSON.stringify(supplied))}`: "";
    const realmParam = `templateRealm=${this.template.realm}`;
    const templateIdParam = `templateId=${this.template.id}`;

    const params = [connIdParam, contextParam, suppliedParam, realmParam, templateIdParam];
    return params.filter(p => p).join('&');
  }

  async getTemplateAttachmentUrl(url) {
    const templateConn = await this.getTemplateConnection();
    return `${this.fe.getRequestFuncsUrl()}getAttachmentContent?url=${encodeURIComponent(url)}&${templateConn}&t=${new Date().getTime()}`;
  }

  getTemplateList(request) {
    return this.fe.httpsCallable('getTemplateList', request);
  }

  templateWorkspaceUpdate(request) {
    return this.fe.httpsCallable('templateWorkspaceUpdate', request);
  }

  linkManagement(request) {
    return this.fe.httpsCallable('linkManagement', request);
  }

  setTemplateData(data) {
    data.template.conditions = this.filterOrphanConditions(data.template);
    this._templateData = data;
    this._originalTemplateData = angular.copy(this._templateData);
    this._templateHasChanges = false;
  }

  removeTempObjects(templateData) {
    const t = templateData.template;
    ["tokens", "references", "relations"].forEach(key => t[key] = t[key].filter(o => !o._temp));
  }
  
  filterOrphanConditions(template) {
    const {tokens, conditions} = template;
    const tokenIds = tokens.map(t => t.id);
    const content = template.content || '';
    return conditions.filter(c => tokenIds.includes(c.parentId) && (tokenIds.includes(c.childId) || content.includes(c.childId)));
  }
  
  initializeNewToken(referenceId, fieldId, pageId=this.pages[0]){
    const { templateId, '_dummyControl': control } = this;
    return this.initializeNewObject({
      templateId, pageId, referenceId, fieldId, control
    });
  }
  
  initializeNewObject(object) {
    const userEmail = this.as.getProfile()?.email;
    return {
      ...object,
      id: this.getNewObjectId(),
      realm: this.template.realm,
      provider: this.template.provider,
      createdBy: userEmail,
      createdAt: 0,
      updatedBy: userEmail,
      updatedAt: 0,
      status: 'active'
    };
  }

  getNewObjectId() {
    return this.uuid.v4();
  }

  dispose() {
    this._templateData = {template: {}};
    this._templateId = null;
  }

  notifyUpdate(keys, newValue, ignoreChanges, extra) {
    this.es.dispatchEvent(this.es.actions.TEMPLATE_DATA_UPDATED, { keys, newValue, ignoreChanges, extra });
  }

  subscribeUpdate(callback, id) {
    this.es.subscribeEvent(this.es.actions.TEMPLATE_DATA_UPDATED, callback, id);
  }

  templateHasChanges(current) {
    const original = this._originalTemplateData?.template;
    return original && (this._templateHasChanges
      || original.title !== current.title
      || original.description !== current.description
      || original.successMessage !== current.successMessage);
  }

  updateTemplateData(templateData, options) {
    const keys = !options.keys?.length ? Object.keys(templateData.template) : options.keys;
    keys.forEach(k => {
      this._templateData.template[k] = options.newValue.template[k];
      templateData.template[k] = options.newValue.template[k];
    });
    if (options.ignoreChanges !== true) this._templateHasChanges = true;
  }
  
  copyToken(origToken) {
    const newToken = angular.copy(origToken);
    Object.keys(newToken).forEach(key => {
      if (key.startsWith("$") || key === 'id') { delete newToken[key] }
    });
    return newToken;
  }

  setTokenBehaviorsFlag(token) {
    if (!token?.id) return;
    token._hasBehavior = this.behaviors.some(b => b.childId === token.id);
  }

  removeToken(tokenId, keepChildren) {
    const t = this._templateData.template;
    t.tokens = t.tokens.filter(t => t.id !== tokenId);
    if (keepChildren) return;
    const condBehaviorsToRemove = t.conditions
      .filter(c => c.childId === tokenId || c.parentId === tokenId)
      .map(c => c.behaviorId);
    t.conditions = t.conditions.filter(c => !condBehaviorsToRemove.includes(c.behaviorId));
    t.behaviors = t.behaviors.filter(b => !condBehaviorsToRemove.includes(b.id) && b.childId !== tokenId);
  }

  removeBehavior(behaviorId) {
    const t = this._templateData.template;
    t.conditions = t.conditions.filter(c => c.behaviorId !== behaviorId);
    t.behaviors = t.behaviors.filter(b => b.id !== behaviorId);
  }

  removePage(pageId) {
    const t = this._templateData.template;
    t.partialPages = t.partialPages.filter(p => p.id !== pageId);
  }

  removeReference(refId) {
    const t = this._templateData.template;
    t.relations = t.relations.filter(r => r.parentId !== refId && r.childId !== refId);
    t.references = t.references.filter(r => r.id !== refId);
  }

  clearTemplateObjects(templateData) {
    templateData.template.references = [];
    templateData.template.relations = [];
    templateData.template.behaviors = [];
    templateData.template.conditions = [];
    templateData.template.tokens = [];
  }

  invalidRefs(templateData) {
    const activeRefIds = [...new Set(templateData.template.tokens.map(t => t.referenceId))];
    return templateData.template.references.filter(r => (!activeRefIds.includes(r.id) && !r.uploadFID));
  }

  setNewConnection(values) {
    this._newConnection = values;
  }

  clearNewConnection() {
    this._newConnection = null;
  }

  getNewConnection() {
    return this._newConnection || [];
  }

  async saveConnection(template, promptValues) {
    const request = { template: this.toRaw({template}), promptValues };
    const response = await this.fe.httpsCallable('storeTemplateConnection', request);
    return response.data.connectionId;
  }

  fetchControls(updatedTokens) {
    const request = {
      template: this.toRaw({ includeRefs: true }),
      updatedTokens
    };
    return this.fe.httpsCallable('fetchControls', request);
  }
  
  tokenRef({referenceId}={}) {
    return this.refById(referenceId);
  }
  
  refById(id) {
    return this.references.find(ref => ref.id === id);
  }
  
  refTokens(referenceId) {
    this.tokens.filter(t => t.referenceId === referenceId);
  }
  
  refTableSchema(tableId) {
    this.references.find(r => r.tableId === tableId)?.tableSchema;
  }
  
  saveTemplate(template) {
    return this.fe.httpsCallable('saveTemplate', {template});
  }
  
  async saveTemplateData(templateData) {
    const template = angular.copy(templateData.template);
    template.conditions = this.filterOrphanConditions(template);

    Object.keys(templateData.template).forEach(k => {
      const prop = template[k];
      if (angular.isArray(prop) && this._originalTemplateData?.template[k]) {
        const missingItems =
          this._originalTemplateData.template[k]
            .filter(o => !prop.some(n => n.id === o.id))
            .map(e => { return { ...e, status: 'removed' } });
        if (missingItems.length > 0) template[k] = [...template[k], ...missingItems];
      }
    });

    const response = await this.saveTemplate(template);
    this.setTemplateData(response.data);
    this._templateDataPromise = Promise.resolve(response.data);
    return await this.getTemplateData();
  }
  
  subscribeEvents() {
    this.es.subscribeEvent(
      this.es.actions.LOCATION_CHANGED, ({templateId}) => {
        this.getTemplateData({templateId});
      }, 'LOCATION_CHANGED_TS'
    );
  }

  getConnLabel(propLabels, propNames) {
    if (!propNames || !propNames.length) return null;
    for (let k of Object.keys(propLabels)) {
      if (propNames.includes(k)) return propLabels[k];
    }
    return null;
  }

  getDynamicTemplateBlockIds() {
    return $('[data-block-id]')
      .map(function () { return $(this).attr('data-block-id') })
      .get();
  }

  restoreTemplateVersion(template, versionToRestore, createNewVersion) {
    const request = { template, version: { versionToRestore, createNewVersion }};
    return this.fe.httpsCallable('saveTemplate', request);
  }

  getFolderPath(folderId, folders) {
    if (!folderId || !folders || !Array.isArray(folders)) return [];
    const path = [];
    let currentFolderId = folderId;
    while (currentFolderId) {
      const folder = folders.find(f => f.id === currentFolderId);
      if (!folder) break;
      path.unshift({ id: folder.id, title: folder.title });
      currentFolderId = folder.parentId;
    }
    return path;
  }

  getFolderPathString(folderId, folders, separator = ' / ') {
    const path = this.getFolderPath(folderId, folders);
    return path.length > 0 ? path.map(f => f.title).join(separator) : '';
  }
}